/**
 * @fileoverview WordPills: the app's one row of tappable word pills, each in its own palette colour, with at most one ringed as
 * the one chosen (rule 8: one shared piece). It is given the words and colours; it fetches nothing and has no opinion about
 * what a tap means. The gardener's note shortcuts (QuickNoteChips) and the Add / Edit sheet's job pills (JobChips) both draw
 * through it, so they look and press the same.
 *
 * A tap stops here: the pills sit inside a good deal of other clickable furniture on the cards, so it never reaches it.
 *
 * Where: QuickNoteChips, JobChips.
 * API: none.
 * Settings: none.
 * Words: none; the caller gives the pills' words and, when it wants them, the row's name and the chosen pill's.
 *
 * @module WordPills
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Pill } from '@/shared/components/ui/Pill';
import { themeForColorKey } from '@/shared/theme/colorPalette';

/** One pill: a stable key, its words and its palette colour key. */
export interface WordPillItem {
  /** Stable key (a shortcut's id, a job's code). */
  key: string;
  /** The words on the pill, as the gardener or the server wrote them. */
  label: string;
  /** The pill palette's colour key. */
  colorKey: string;
}

/** The row's props. */
export interface WordPillsProps {
  /** The pills, in order. */
  items: WordPillItem[];
  /** The key of the ringed pill; none (or one not in the row) rings nothing. */
  selectedKey?: string | null;
  /** A tap on a pill: its item. */
  onPick: (item: WordPillItem) => void;
  /** Greys the pills while what they would go into is being saved. */
  disabled?: boolean;
  /** Names the row for a screen reader (the row is then a group). */
  label?: string;
  /** The ringed pill's own aria-label, given its words ("Feed, chosen"). */
  chosenLabel?: (words: string) => string;
  /** Extra classes for the row, for the caller's spacing. */
  className?: string;
  /** Something after the last pill in the same row (QuickNoteChips' + pill and its panel). */
  children?: React.ReactNode;
}

/**
 * The row of word pills.
 *
 * @param props - WordPillsProps.
 * @returns The row, or nothing when there is no pill and nothing after them.
 */
export const WordPills: React.FC<WordPillsProps> = ({ items, selectedKey = null, onPick, disabled = false, label, chosenLabel, className, children }) => {
  if (items.length === 0 && !children) return null;
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)} role={label ? 'group' : undefined} aria-label={label}>
      {items.map((item) => {
        const chosen = item.key === selectedKey;
        return (
          <Pill
            key={item.key}
            size="sm"
            theme={themeForColorKey(item.colorKey)}
            selected={chosen}
            aria-label={chosen && chosenLabel ? chosenLabel(item.label) : undefined}
            onClick={(e) => {
              // The tap stops here rather than reaching the clickable furniture around the row.
              e.preventDefault();
              e.stopPropagation();
              onPick(item);
            }}
            disabled={disabled}
          >
            {item.label}
          </Pill>
        );
      })}
      {children}
    </div>
  );
};
